/**
 * Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
 * analytics events keep the historical "owner note" name on purpose — do not rename them.
 *
 * The owner-note question set, fetched once per node per page lifetime and shared by every
 * surface that needs it (the band, the PDP section's labels, a preload on idle). The band
 * used to fetch on open, so "Leave an owner review" showed "Loading…" for a round trip to
 * DealHunt; with a preload it opens with the questions already painted.
 *
 * The fetch is NOT tied to a caller's AbortSignal: it's shared, so one unmounting caller
 * must not cancel it for the others — callers drop a late answer with their own flag. A
 * miss or a failure is evicted so the next open retries.
 */
import { fetchOwnerQuestionSet } from 'lib/dealhunt/client'
import type { OwnerQuestionSetDef } from 'lib/dealhunt/types'

const settled = new Map<string, OwnerQuestionSetDef>()
const inflight = new Map<string, Promise<OwnerQuestionSetDef | null>>()

/** The questions when already loaded — lets the band's first render skip "Loading…". */
export function peekQuestionSet(nodeId: string): OwnerQuestionSetDef | null {
  return settled.get(nodeId) ?? null
}

/** The questions for a node (null = no question set for its category). Shared in flight. */
export function loadQuestionSet(nodeId: string): Promise<OwnerQuestionSetDef | null> {
  const hit = settled.get(nodeId)
  if (hit) {
    return Promise.resolve(hit)
  }
  const pending = inflight.get(nodeId)
  if (pending) {
    return pending
  }
  const request = fetchOwnerQuestionSet(nodeId)
    .then(res => {
      if (res) {
        settled.set(nodeId, res.questions)
      }
      return res?.questions ?? null
    })
    .finally(() => inflight.delete(nodeId))
  inflight.set(nodeId, request)
  return request
}

/** Warm the question set without a caller waiting on it (errors are the next open's). */
export function preloadQuestionSet(nodeId: string): void {
  loadQuestionSet(nodeId).catch(() => undefined)
}

/** Test seam: forget everything loaded. */
export function resetQuestionSets(): void {
  settled.clear()
  inflight.clear()
}
